import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Button, Card, Notice, PageTitle, Spinner } from "../../ui/index.js";
import { openPaddleCheckout } from "./paddle.js";

/** Тексты страницы внутри фичи, чтобы не править общие `locales/*.json` параллельно с другими этапами. */
const text = {
  "sr-Latn": {
    title: "Pretplata",
    status: {
      none: "Nema pretplate",
      trialing: "Probni period",
      active: "Aktivna",
      past_due: "Plaćanje nije prošlo",
      paused: "Pauzirana",
      canceled: "Otkazana",
    },
    trialUntil: "Probni period do",
    nextCharge: "Sledeća naplata",
    cancelAt: "Otkazuje se",
    priceTrial: "€25 mesečno, prvi mesec probni. Kartica se unosi odmah, naplata posle probnog perioda.",
    priceNoTrial: "€25 mesečno, naplata odmah (probni period je već iskorišćen).",
    setupFee: "Uz prvu uplatu: „povežemo za vas“ €30 jednokratno.",
    pay: "Pokreni pretplatu",
    manage: "Upravljaj pretplatom (kartica, otkazivanje)",
    mock: "Plaćanje još nije povezano (nema Paddle ključeva). Obratite se podršci.",
    paid: "Hvala! Status će se ažurirati za nekoliko sekundi.",
    error: "Nešto nije u redu. Pokušajte ponovo.",
    mor: "Plaćanja obrađuje Paddle.com (Merchant of Record).",
  },
  en: {
    title: "Subscription",
    status: { none: "No subscription", trialing: "Trial", active: "Active", past_due: "Payment failed", paused: "Paused", canceled: "Cancelled" },
    trialUntil: "Trial until",
    nextCharge: "Next charge",
    cancelAt: "Cancels on",
    priceTrial: "€25 per month, first month is a trial. Card required now, first charge after the trial.",
    priceNoTrial: "€25 per month, charged now (the trial has already been used).",
    setupFee: "With the first payment: “done for you” setup, €30 one-time.",
    pay: "Start subscription",
    manage: "Manage subscription (card, cancel)",
    mock: "Payments are not connected yet (no Paddle keys). Please contact support.",
    paid: "Thank you! The status will update in a few seconds.",
    error: "Something went wrong. Please try again.",
    mor: "Payments are processed by Paddle.com (Merchant of Record).",
  },
  ru: {
    title: "Подписка",
    status: { none: "Подписки нет", trialing: "Пробный период", active: "Активна", past_due: "Платёж не прошёл", paused: "На паузе", canceled: "Отменена" },
    trialUntil: "Пробный период до",
    nextCharge: "Следующее списание",
    cancelAt: "Отменится",
    priceTrial: "€25 в месяц, первый месяц пробный. Карта нужна сразу, списание после пробного периода.",
    priceNoTrial: "€25 в месяц, списание сразу (пробный период уже был).",
    setupFee: "Вместе с первой оплатой: «подключим за вас» €30 разово.",
    pay: "Оформить подписку",
    manage: "Управлять подпиской (карта, отмена)",
    mock: "Оплата ещё не подключена (нет ключей Paddle). Напишите в поддержку.",
    paid: "Спасибо! Статус обновится через несколько секунд.",
    error: "Что-то пошло не так. Попробуйте ещё раз.",
    mor: "Платежи обрабатывает Paddle.com (Merchant of Record).",
  },
} as const;

type Lang = keyof typeof text;

/** Подписка салона для владельца: статус, оплата через Paddle (оверлей), портал Paddle для карты и отмены. */
export function BillingPage() {
  const { i18n } = useTranslation();
  const lang: Lang = i18n.language in text ? (i18n.language as Lang) : "sr-Latn";
  const t = text[lang];
  const { salonId = "" } = useParams();
  const [params] = useSearchParams();
  const [justPaid, setJustPaid] = useState(params.get("paid") === "1");
  const [mock, setMock] = useState(false);

  // После оплаты статус приходит вебхуком: пока ждём, опрашиваем.
  const status = useQuery({ ...trpc.billing.status.queryOptions({ salonId }), refetchInterval: justPaid ? 3000 : false });
  const checkout = useMutation(trpc.billing.checkout.mutationOptions());
  const portal = useMutation(trpc.billing.portalUrl.mutationOptions());
  const statusName = status.data?.status;
  useEffect(() => {
    // Вебхук дошёл, опрос больше не нужен.
    if (statusName === "trialing" || statusName === "active") setJustPaid(false);
  }, [statusName]);

  if (status.isPending) return <Spinner />;
  if (status.isError) return <Notice kind="error">{t.error}</Notice>;
  const s = status.data;
  const live = s.status === "trialing" || s.status === "active" || s.status === "past_due" || s.status === "paused";
  const fmt = (d: Date | null) => (d ? new Intl.DateTimeFormat(lang === "sr-Latn" ? "sr-Latn-RS" : lang, { dateStyle: "medium" }).format(d) : "–");

  const onPay = async () => {
    const cfg = await checkout.mutateAsync({ salonId });
    if (cfg.mode === "mock") {
      setMock(true);
      return;
    }
    await openPaddleCheckout({
      ...cfg,
      successUrl: `${window.location.origin}${window.location.pathname}?paid=1`,
      locale: lang === "sr-Latn" ? "sr" : lang,
      onEvent: (name) => {
        if (name === "checkout.completed") setJustPaid(true);
      },
    });
  };
  const onManage = async () => {
    const { url } = await portal.mutateAsync({ salonId });
    window.location.assign(url);
  };

  return (
    <div className="lg:max-w-3xl">
      <PageTitle>{t.title}</PageTitle>
      <Card className="mb-4">
        <div className="mb-2 text-lg font-medium">{t.status[s.status as keyof typeof t.status] ?? s.status}</div>
        {s.status === "trialing" && (
          <div className="text-sm text-ink-2">
            {t.trialUntil}: {fmt(s.trialEndsAt)}
          </div>
        )}
        {live && !s.cancelAt && s.currentPeriodEnd && (
          <div className="text-sm text-ink-2">
            {t.nextCharge}: {fmt(s.currentPeriodEnd)}
          </div>
        )}
        {s.cancelAt && (
          <div className="text-sm text-ink-2">
            {t.cancelAt}: {fmt(s.cancelAt)}
          </div>
        )}
      </Card>
      {justPaid && (
        <div className="mb-4">
          <Notice kind="success">{t.paid}</Notice>
        </div>
      )}
      {mock && (
        <div className="mb-4">
          <Notice>{t.mock}</Notice>
        </div>
      )}
      {(checkout.isError || portal.isError) && (
        <div className="mb-4">
          <Notice kind="error">{t.error}</Notice>
        </div>
      )}
      {!live && (
        <>
          <p className="mb-2 text-sm text-ink-2">{s.offer.trial ? t.priceTrial : t.priceNoTrial}</p>
          {s.offer.setupFee && <p className="mb-4 text-sm text-ink-2">{t.setupFee}</p>}
          <Button className="mb-4 w-full lg:w-auto" onClick={() => void onPay().catch(() => {})} disabled={checkout.isPending}>
            {t.pay}
          </Button>
        </>
      )}
      {s.hasCustomer && (
        <Button variant="secondary" className="mb-4 w-full lg:w-auto" onClick={() => void onManage().catch(() => {})} disabled={portal.isPending}>
          {t.manage}
        </Button>
      )}
      <p className="text-xs text-ink-3">{t.mor}</p>
    </div>
  );
}
